import { HttpClient } from "../http/http-client/http-client";
import { HttpResponse } from "../http/http-response/http-response";
import { HttpUtilService } from "../http/http-util-service";
import { Logger } from '../../logger/logger';
import { ApiParser } from '../../util/api-parser';
import { AgentSettings } from './model/agent-settings';
import { Permissions } from './model/permissions';
import { AdminApis } from './model/admin-apis';
import { CXoneAuthBase } from "../authenticate/cxone-auth-base";
import { BusinessUnit } from "./model/business-unit";
import { StorageKeys } from "../../constants/storage-key";
import { CentralBrandingProfile } from "./model/central-branding-profile";
import { BrandingProfile } from "./model/branding-profile";
import { CXoneIndicator } from "@nice-ccf/common-sdk";

/**
 * Class to perform get admin api
 */
export class AdminService {
    protected logger: Logger = new Logger('SDK', 'AdminService');
    protected utilService: HttpUtilService = new HttpUtilService();
    private apiParser = new ApiParser();
    private authBase: CXoneAuthBase;
    private agentIndicatorsInfo: CXoneIndicator[] = [];
    private static singleton: AdminService;
    public static cxoneAuth: CXoneAuthBase;
    /**
     * 
     * @param authBaseObject - Instance of CXoneAuthByCode
     * ```
     * @example
     * const adminService = new AdminService(CXoneAuthByCode);
     * ```
     */
    constructor(authBaseObject: CXoneAuthBase) {
        this.authBase = authBaseObject;
    }

   /**
   * Method to create singleton object of the class
   * ```
   * @example
   * const agentSession = AdminService.instance();
   * ```
   */
    public static get instance(): AdminService{
        if(!AdminService.singleton) {
          this.cxoneAuth = CXoneAuthBase.Instance;
          AdminService.singleton = new AdminService(this.cxoneAuth);
        }
        return AdminService.singleton;
      }

  /**
   * Method to return agent permissions
   * @returns - return the agent permissions
   * ```
   * @example
   * getPermissions()
   * ```
   */
  public getPermissions(forceFetch = false): Promise<Permissions[]> {
    return new Promise<Permissions[]>((resolve, reject) => {
        const permissionFromStorage = localStorage.getItem(StorageKeys.PERMISSIONS);
        if (forceFetch || !permissionFromStorage) {
            const token = this.authBase.getAuthToken();
            const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
            const user = this.authBase.getUserDetails();
            const cxOneConfig = this.authBase.getCXoneConfig();
            const permissionUrl = cxOneConfig.acdApiBaseUri + AdminApis.getPermissionsUri.replace('{agentId}', user.icAgentId);
            HttpClient.get(permissionUrl, reqInit).then((resp: HttpResponse) => {
                this.logger.info('getPermissions', 'Get Permissions Success');
                const permission = this.apiParser.parsePermissions(resp);
                localStorage.setItem(StorageKeys.PERMISSIONS, JSON.stringify(permission));
                resolve(permission);
            },
            (err: HttpResponse) => {
                this.logger.error('getPermissions', 'Get Permissions Failed ' + JSON.stringify(err));
                reject(err);
            });
        }
        else {
            this.logger.info('getPermissions', 'Get Permissions from storage');
            resolve(JSON.parse(permissionFromStorage));
        }
    });
  }

    /**
   * Method to return agent settings
   * @returns - return the agent settings
   * ```
   * @example
   * getAgentSettings()
   * ```
   */
  public getAgentSettings(): Promise<AgentSettings> {
    return new Promise<AgentSettings>((resolve, reject) => {
        const agentSettingFromStorage = localStorage.getItem(StorageKeys.AGENT_SETTINGS);
        if (!agentSettingFromStorage) {
            const token = this.authBase.getAuthToken();
            const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
            const user = this.authBase.getUserDetails();
            const cxOneConfig = this.authBase.getCXoneConfig();
            const permissionUrl = cxOneConfig.acdApiBaseUri + AdminApis.getAgentSettingsUri.replace('{agentId}', user.icAgentId);
            HttpClient.get(permissionUrl, reqInit).then((resp: HttpResponse) => {
                this.logger.info('getPermissions', 'Get Agent settings Success');
                const agentSettings = this.apiParser.parseAgentSettings(resp);
                localStorage.setItem(StorageKeys.AGENT_SETTINGS, JSON.stringify(agentSettings));
                resolve(agentSettings);
            },
            (err: HttpResponse) => {
                this.logger.error('getPermissions', 'Get Agent settings Failed ' + JSON.stringify(err));
                reject(err);
            });
        }
        else {
            this.logger.info('getPermissions', 'Get Agent settings from storage');
            resolve(JSON.parse(agentSettingFromStorage));
        }
    });
  }

  /**
   * Method to return business unit features
   * @returns - returns the business unit features
   * ```
   * @example
   * getBusinessUnit()
   * ```
   */
  public getBusinessUnit(): Promise<BusinessUnit> {
    return new Promise<BusinessUnit>((resolve, reject) => {
        const businessUnitFromStorage = localStorage.getItem(StorageKeys.BUSINESS_UNIT);
        if(!businessUnitFromStorage) {
            const token = this.authBase.getAuthToken();
            const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
            const cxoneConfig = this.authBase.getCXoneConfig();
            const url = cxoneConfig.acdApiBaseUri + AdminApis.getBusinessUnitUri;
            HttpClient.get(url, reqInit).then(
                (resp: HttpResponse) => {
                    this.logger.info('getBusinessUnit', 'Get Business Unit Success');
                    const data = this.apiParser.parseBusinessUnit(resp);
                    localStorage.setItem(StorageKeys.BUSINESS_UNIT, JSON.stringify(data));
                    resolve(data);
                },
                (err: HttpResponse) => {
                    this.logger.error('getBusinessUnit', 'Get Business Unit Failed' + JSON.stringify(err));
                    reject(err);
                }
            );
        }
    });
    }

  /**
   * Method to return client data
   * @returns - returns the client data
   * ```
   * @example
   * getClientData()
   * ```
   */
  public getClientData(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const clientDataFromStorage = localStorage.getItem(StorageKeys.CLIENT_DATA);
        if(!clientDataFromStorage) {
            const token = this.authBase.getAuthToken();
            const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
            const cxoneConfig = this.authBase.getCXoneConfig();
            const user = this.authBase.getUserDetails();
            const url = cxoneConfig.acdApiBaseUri + AdminApis.getClientDataUri.replace('{agentId}', user.icAgentId);
            HttpClient.get(url, reqInit).then(
                (resp: HttpResponse) => {
                    this.logger.info('getClientData', 'Get Client Data Success');
                    localStorage.setItem(StorageKeys.CLIENT_DATA, JSON.stringify(resp));
                    resolve(resp.data);
                },
                (err: HttpResponse) => {
                    this.logger.error('getClientData', 'Get Client Data Failed' + JSON.stringify(err));
                    reject(err);
                }
            );
        }
    });
    }

     /**
   * Method to return central branding profile
   * @returns - returns the central branding profile
   * ```
   * @example
   * getCentralBrandingProfile()
   * ```
   */
  public getCentralBrandingProfile(): Promise<CentralBrandingProfile>{
    return new Promise<CentralBrandingProfile>((resolve, reject) => {
        const centralBrandingProfileFromStorage = localStorage.getItem(StorageKeys.BRANDING_PROFILES);
        if(!centralBrandingProfileFromStorage) {
            const token = this.authBase.getAuthToken();
            const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
            const cxoneConfig = this.authBase.getCXoneConfig();
            const user = this.authBase.getUserDetails();
            const url = cxoneConfig.acdApiBaseUri + AdminApis.getCentralBrandingProfileUri.replace('{businessUnitId}', user.icBUId);
            HttpClient.get(url, reqInit).then(
                (resp: HttpResponse) => {
                    this.logger.info('getCentralBrandingProfile', 'Get Central Branding Profile Success');
                    const data = this.apiParser.parseCentralBrandingProfile(resp);
                    localStorage.setItem(StorageKeys.BRANDING_PROFILES, JSON.stringify(data));
                    resolve(data);
                },
                (err: HttpResponse) => {
                    this.logger.error('getCentralBrandingProfile', 'Get Central Branding Profiles Failed' + JSON.stringify(err));
                    reject(err);
                }
            );
        }
    });
  }

  /**
   * Method to return cxone branding profiles
   * @returns - returns the cxone branding profiles
   * ```
   * @example
   * getBrandingProfile()
   * ```
   */
  public getBrandingProfile(): Promise<BrandingProfile>{
    return new Promise<BrandingProfile>((resolve, reject) => {
        const BrandingProfileFromStorage = localStorage.getItem(StorageKeys.BRANDING_PROFILES);
        if(!BrandingProfileFromStorage) {
            const token = this.authBase.getAuthToken();
            const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
            const cxoneConfig = this.authBase.getCXoneConfig();
            const url = cxoneConfig.userHubBaseUrl + AdminApis.getUHBrandingProfileUri;
            HttpClient.get(url, reqInit).then(
                (resp: HttpResponse) => {
                    this.logger.info('getBrandingProfile', 'Get Branding Profile Success');
                    const data = this.apiParser.parseBrandingProfile(resp);
                    localStorage.setItem(StorageKeys.BRANDING_PROFILES, JSON.stringify(data));
                    resolve(data);
                },
                (err: HttpResponse) => {
                    this.logger.error('getBrandingProfile', 'Get Branding Profiles Failed' + JSON.stringify(err));
                    reject(err);
                }
            );
        }
    });
 }

  /**
   * Method to call the script to get the skill Id
   * @example
   * ```
   * getScriptData('scriptName');
   * ```
   */
   public getScriptByName(scriptName: string): Promise<number> {
    const baseUrl =  this.authBase.getCXoneConfig().acdApiBaseUri;
    const authToken = this.authBase.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken, 'application/json');
    const url = baseUrl + AdminApis.getScriptData + '?scriptName=' + scriptName;
    return new Promise((resolve, reject) => {
      HttpClient.get(url, reqInit).then(
        (response: HttpResponse) => {
          const scriptId:number = response.data.scripts[0].scriptId
          this.logger.debug('getScriptData', 'getScriptData success:-' + response.toString());
          resolve(scriptId);
        },
        (error) => {
          this.logger.error('getScriptData', 'getScriptData failed:-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to call spawn script
   * @example
   * ```
   * spawnScript();
   * ```
   */
  public spawnScript(scriptId: number, skillId: number, parameters: string, startDate: string): Promise<HttpResponse> {
    const baseUrl =  this.authBase.getCXoneConfig().acdApiBaseUri;
    const authToken = this.authBase.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken, 'application/json');
    reqInit.body = {
      parameters: parameters,
      scriptId: scriptId,
      skillId: skillId,
      startDate: startDate,
    };
    const url = baseUrl + AdminApis.spawnScriptUri.replace('{scriptId}', scriptId.toString());
    return new Promise((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.debug('spawnScript', 'spawnScript success:-' + response.toString());
          resolve(response);
        },
        (error) => {
          this.logger.error('spawnScript', 'spawnScript failed:-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to start the signal script
   * @param contactId -  contact id
   * @example
   * ```
   * signalScript('123', {});
   * ```
   */
  public signalScript(contactId: string, parameters: any): Promise<HttpResponse> {
    const baseUrl =  this.authBase.getCXoneConfig().acdApiBaseUri;
    const authToken = this.authBase.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken, 'application/json');
    const url = baseUrl + AdminApis.signalScriptUri.replace('{contactId}', contactId);
    const requestParams = parameters;
    reqInit.body = requestParams;
    return new Promise((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.debug('startSignalScript', 'startSignalScript success:-' + response.toString());
          resolve(response);
        },
        (error) => {
          this.logger.error('startSignalScript', 'startSignalScript failed:-' + error.toString());
          reject(error);
        }
      );
    });
  }
 /**
   * Method to return all indicators
   * @returns - returns all indicators
   * ```
   * @example
   * getAllIndicators()
   * ```
   */
  public getAllIndicators(): Promise<CXoneIndicator[]>{
    return new Promise<CXoneIndicator[]>((resolve, reject) => {
        const token = this.authBase.getAuthToken();
        const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
        const cxoneConfig = this.authBase.getCXoneConfig();
        const user = this.authBase.getUserDetails();
        const url = cxoneConfig.acdApiBaseUri + AdminApis.getAllIndicatorsUri.replace('{agentId}', user.icAgentId);
        HttpClient.get(url, reqInit).then(
            (resp: any) => {
                this.agentIndicatorsInfo = [];
                this.logger.info('getAllIndicators', 'Get All Indicators Success');
                if (resp && resp.body && resp.body.indicators) {
                    resp.body.indicators.forEach((indicator: any) => {
                    const agentIndicators = new CXoneIndicator();
                    agentIndicators.parse(indicator);
                    this.agentIndicatorsInfo.push(agentIndicators);
                  });
                  resolve(this.agentIndicatorsInfo);
                }
            },
            (err: HttpResponse) => {
                this.logger.error('getAllIndicators', 'Get All Indicators Failed' + JSON.stringify(err));
                reject(err);
            }
        );
    });
 }
}
